import { memo, useState } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import CustomTooltip from "shared/components/CustomTooltip";
import styled, { css } from "styled-components/macro";
import { Button, GenericButton, IconDispatcher } from "../ui-basics";

const BasicActionsDropdown = memo(({ id = "", actions, dropdownClasses = "", size = "m", stopPropagation = false, isDisabled = false }) => {
	const [showDropdown, setShowDropdown] = useState(false);
	const handleDropdownToggle = (isOpen, event) => {
		if (stopPropagation) event.originalEvent.stopPropagation();
		setShowDropdown(isOpen);
	};

	const handleClickItem = (action, e) => {
		e.stopPropagation();
		if (action.handleClick) {
			action?.handleClick(e);
		}
		if (action.customItem) return;
		setShowDropdown(!showDropdown);
	};
	const DisplayDropdownItem = ({ action, index }) => {
		if (action?.divider) return <Divider className="mx-2 my-1" />;
		if (action.header) return <Header>{action.header}</Header>;

		return (
			<CustomTooltip text={action?.tooltipText} disabled={!action?.tooltipText}>
				<DropdownItem
					key={index}
					variant={action?.itemStyle}
					className={action?.itemClasses}
					eventKey={action?.eventKey}
					onClick={(e) => handleClickItem(action, e)}
					disabled={action?.disabled ? action.disabled : false}
					clickable={action?.clickable ? action.clickable.toString() : "true"}
					draggable="false" // Add this attribute to disable dragging
				>
					{action?.customItem ? (
						<div className="item">{action.customItem}</div>
					) : (
						<div className="item">
							<GenericButton variant="actiondropdown" disabled={action?.disabled} icon={action?.icon && action?.icon} iconClassName={action?.iconStyle}>
								<TextWrapper type={action?.type}>{action?.text}</TextWrapper>
							</GenericButton>
						</div>
					)}
				</DropdownItem>
			</CustomTooltip>
		);
	};

	return (
		<DropdownWrapper id={`original-dropdown_${id}`} className={dropdownClasses} drop="bottom" show={showDropdown} onToggle={handleDropdownToggle} autoClose="true">
			<Dropdown.Toggle as={Button} id={`dropdown-toggle-${id}`} variant="light-link" isicon={"true"} size={size} disabled={isDisabled || actions?.length === 0}>
				{IconDispatcher("ellipsis")}
			</Dropdown.Toggle>
			<DropdownMenu id={`dropdown-menu-${id}`} variant="sidebar">
				{actions?.map((action, index) => (
					<DisplayDropdownItem key={index} action={action} index={index} />
				))}
			</DropdownMenu>
		</DropdownWrapper>
	);
});

BasicActionsDropdown.displayName = "BasicActionsDropdown";
export { BasicActionsDropdown };

const DropdownWrapper = styled(Dropdown)`
	margin: 0 !important;
	display: flex;

	.dropdown-toggle:after {
		display: none;
	}

	.dropdown-item:hover {
		visibility: visible;
	}

	.dropdown-menu.show {
		padding: 0 !important;
		box-shadow: 1px 1px 3px rgba(33, 33, 33, 0.3);
	}
	.dropdown-item:focus,
	.dropdown-item:hover {
		background-color: #efefef;
	}
`;

const DropdownMenu = styled(Dropdown.Menu)`
	z-index: 1087;
	padding: 4px;
	${(props) =>
		props.variant === "sidebar" &&
		css`
			border-radius: 4px;
			border: 1px solid #c4c4c4;
			background: #fff;
			box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.15);
			.btn {
				color: #333333;
				font-family: Inter;
				font-size: 14px;
				font-style: normal;
				font-weight: 400;
			}
		`}
`;

const Header = styled(Dropdown.Header)`
	font-size: 12px;
	font-weight: 500;
	color: #333333;
	padding: 4px 12px;
`;

const DropdownItem = styled.div`
	width: 100%;
	padding: 4px;
	clear: both;
	font-weight: 400;
	color: #333333;
	text-align: inherit;
	text-decoration: none;
	white-space: nowrap;
	background-color: transparent;
	border: 0;
	display: block;
	align-items: center;
	justify-content: left;
	line-height: 1.5rem;
	.item {
		width: 100%;
		button {
			height: 29px;
			padding: 4px 8px;
		}
		:hover {
			background-color: #efefef;
		}
	}

	${(props) =>
		props.variant === "warning-delete" &&
		`
      path {
         fill: #FC74B3
      }

   `}
	${(props) =>
		props.disabled
			? css`
					pointer-events: none;
					opacity: 0.5;
			  `
			: ""}
    ${(props) =>
		props.clickable === "false" &&
		css`
			pointer-events: none;
		`}
`;

const TextWrapper = styled.div`
	margin-left: 8px;
	${(props) =>
		props.type === "email" &&
		css`
			text-transform: lowercase;
		`}
	${(props) =>
		props.type === "needHelp" &&
		css`
			text-transform: none;
		`}
    ${(props) =>
		props.type === "textTransformNone" &&
		css`
			text-transform: none;
		`}
`;

const Divider = styled.div`
	border-bottom: 1px solid #d9d9d9;
`;
